import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { PencilIcon } from "@heroicons/react/outline";
import DetailSkeleton from "../../Components/DetailSkeleton";
import { Alert } from "../../Components/Alert";
import AllowanceModal from "../../Components/v2/Accounting/AllowanceModal";
import Badge from "../../Components/Badge";
import {
  getPTKPRate,
  POSITION_COST_MAX,
  POSITION_COST_PERCENTAGE,
  TAX_BRACKETS,
} from "../../Constants/terRates";
import {
  requestGetBusinessEmployee,
  requestGetEmployeeAttendanceSummary,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
} from "../../Context";
import { currencyFormat } from "../../Utils";
import { buildSalaryYear } from "../../Utils/salaryYear";
import { getAnnualisation, getTaxYearEndDate } from "../../Utils/taxSubject";
import DeductionModal from "../../Components/v2/Accounting/DeductionModal";

const SalaryTable = () => {
  // Hooks
  const { t, i18n } = useTranslation();
  const { id } = useParams();

  // Context and reducer related
  const dispatch = useBusinessTransactionDispatch();
  const {
    employee,
    getEmployeeLoading,
    getEmployeeError,
    attendanceSummary,
    attendanceSummaryLoading,
    attendanceSummaryError,
  } = useBusinessTransactionState();

  // State
  const [allowanceModalOpen, setAllowanceModalOpen] = useState(false);
  const [deductionModalOpen, setDeductionModalOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);

  const isEmployeePanelLoading = getEmployeeLoading || attendanceSummaryLoading;

  // Component level utility functions
  const formatMonth = (date) =>
    dayjs(date).locale(i18n.language).format("MMMM");

  // Constants
  const currentYear = dayjs().year();
  const startMonth =
    dayjs(employee.startDate).year() < currentYear
      ? dayjs().month(0).locale(i18n.language).format("MMMM")
      : formatMonth(employee.startDate);

  // Both labels hang off the same month, so they are derived from it once. When
  // they were two independent ternaries the no-end-date branch fell back to the
  // last tax period itself, and the row holding the Jan-Nov figure was labelled
  // Januari - Desember.
  const taxYearEndDate = employee ? getTaxYearEndDate(employee) : null;
  const lastTaxPeriodMonth =
    taxYearEndDate && taxYearEndDate.year() === currentYear
      ? taxYearEndDate
      : dayjs().month(11);

  const endMonth = formatMonth(lastTaxPeriodMonth);

  const prevMonth = formatMonth(lastTaxPeriodMonth.subtract(1, "month"));

  const showSalaryTable =
    employee?.employmentType === "PERMANENT" &&
    employee?.salaryType === "MONTHLY";

  // Annualization changes the last month's withholding drastically, so it has
  // to be visible on the page. The figures must never shift silently.
  const annualisation = employee?.startDate
    ? getAnnualisation(employee, dayjs().year())
    : { annualise: false, monthsWorked: 12 };

  const isAttendanceUnavailable =
    !attendanceSummaryLoading &&
    (Boolean(attendanceSummaryError) ||
      attendanceSummary?.calendarConfigured === false);

  useEffect(() => {
    if (id) {
      requestGetBusinessEmployee(dispatch, { id });
      requestGetEmployeeAttendanceSummary(dispatch, {
        id,
        year: dayjs().year(),
      });
    }
  }, [dispatch, id]);

  // Component level utility functions
  const handleModalOpen = (monthData, type) => {
    setSelectedMonth(monthData.monthValue);
    type === "allowance"
      ? setAllowanceModalOpen(true)
      : setDeductionModalOpen(true);
  };

  const handleCloseModal = () => {
    setAllowanceModalOpen(false);
    setDeductionModalOpen(false);
    setSelectedMonth(null);
    // Refresh employee data to get updated allowances and deductions
    if (id) {
      requestGetBusinessEmployee(dispatch, { id });
    }
  };

  const calculateTotalPositionCost = (monthlySalaries) => {
    return Math.min(
      monthlySalaries.reduce(
        (sum, item) =>
          sum +
          Math.min(
            parseFloat(item.grossSalary || 0) * POSITION_COST_PERCENTAGE,
            POSITION_COST_MAX / 12,
          ),
        0,
      ),
      POSITION_COST_MAX,
    );
  };

  // The engine lives in Utils/salaryYear.js, shared case for case with the
  // payroll Lambda. This only turns its rows into what the table displays.
  const monthlySalaries = useMemo(
    () =>
      buildSalaryYear({
        employee,
        attendanceSummary,
        year: dayjs().year(),
      }).map((row) => ({
        ...row,
        monthValue: String(row.month).padStart(2, "0"),
        month: dayjs(`${row.key}-01`)
          .locale(i18n.language)
          .format("MMMM YYYY"),
        isDecember: row.isLastTaxPeriod,
        yearlyProgressiveTax: row.yearlyTax,
      })),
    [i18n, employee, attendanceSummary],
  );

  const salaryCalculations = useMemo(() => {
    const reducer = (field, parser = parseFloat) =>
      monthlySalaries.reduce((sum, item) => sum + parser(item[field] || 0), 0);

    const calculations = {
      totalBaseSalary: reducer("baseSalary"),
      totalTaxableAllowances: reducer("taxableAllowances"),
      totalGrossSalary: reducer("grossSalary"),
      totalTaxAmount: reducer("taxAmount"),
      totalAttendanceDeduction: reducer("attendanceDeduction"),
      totalDeductions: reducer("monthlyDeductionAmount"),
      totalPositionCost: calculateTotalPositionCost(monthlySalaries),
      ptkpAmount: getPTKPRate(employee?.marriageStatus, employee?.dependent)
        .amount,
    };

    calculations.grossUpTaxAmount =
      employee?.taxMethod === "GROSS_UP"
        ? Math.floor(calculations.totalTaxAmount)
        : 0;
    calculations.netSalary = Math.floor(calculations.totalGrossSalary);

    // The last tax period already solved for the taxable income the year's tax
    // was assessed on, annualized where PMK 168/2023 Lampiran B angka I.2.1.2
    // requires it. Recomputing it from the yearly totals here would print a
    // taxable income that contradicts the tax charged in the table below.
    const lastTaxPeriod = [...monthlySalaries]
      .reverse()
      .find((item) => item.yearlyTaxableIncome != null);

    calculations.taxableIncome = lastTaxPeriod
      ? lastTaxPeriod.yearlyTaxableIncome
      : Math.floor(
          Math.max(
            0,
            calculations.totalGrossSalary -
              calculations.totalPositionCost -
              calculations.totalDeductions -
              calculations.ptkpAmount,
          ),
        );

    // What Article 17 charges on that taxable income, before the proration
    // annualization is followed by. Equal to the tax due when nothing was
    // annualized.
    calculations.progressiveTaxBeforeProration = TAX_BRACKETS.reduce(
      (sum, bracket, index) => {
        const prevMax = index > 0 ? TAX_BRACKETS[index - 1].max : 0;
        const taxableAmount = Math.min(
          Math.max(0, calculations.taxableIncome - prevMax),
          bracket.max - prevMax,
        );
        return sum + Math.max(0, taxableAmount) * bracket.rate;
      },
      0,
    );

    calculations.previousMonthsTax = Math.floor(
      monthlySalaries
        .slice(0, -1)
        .reduce((sum, item) => sum + parseFloat(item.taxAmount || 0), 0),
    );
    calculations.currentMonthTax = Math.floor(
      parseFloat(monthlySalaries[monthlySalaries.length - 1]?.taxAmount || 0),
    );

    return calculations;
  }, [
    monthlySalaries,
    employee?.taxMethod,
    employee?.marriageStatus,
    employee?.dependent,
  ]);

  const pkp = salaryCalculations.taxableIncome;

  // Without employee data this page genuinely cannot render. Without the
  // attendance summary it can: PPh 21 is calculated on gross income, and the
  // work calendar never enters the formula.
  if (getEmployeeError) {
    return (
      <div className="bg-white min-h-full rounded-lg p-4">
        <Alert
          type="error"
          title="Error"
          message={getEmployeeError?.message}
          errors={getEmployeeError?.errors}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="px-4 py-5 sm:px-6 bg-white rounded-xl overflow-hidden space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-sm font-medium text-gray-400">
              {t("employees.fields.employeeId")}
            </p>
            {isEmployeePanelLoading ? (
              <DetailSkeleton />
            ) : (
              <p className="text-sm font-semibold text-gray-800">
                {employee?.employeeID}
              </p>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-400">
              {t("employees.fields.name")}
            </p>
            {isEmployeePanelLoading ? (
              <DetailSkeleton />
            ) : (
              <p className="text-sm font-semibold text-gray-800">
                {`${employee?.firstName} ${employee?.lastName || ""}`}
              </p>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-400">
              {t("employees.fields.employmentStatus")}
            </p>
            {isEmployeePanelLoading ? (
              <DetailSkeleton />
            ) : (
              <div className="mt-1 text-sm text-gray-900">
                {employee?.employmentStatus === "ON_LEAVE" && (
                  <Badge
                    title={`employees.employmentStatus.leave`}
                    type="alert"
                  />
                )}
                {employee?.employmentStatus === "ACTIVE" && (
                  <Badge
                    title={`employees.employmentStatus.${employee.employmentStatus.toLowerCase()}`}
                    type="success"
                  />
                )}
                {employee?.employmentStatus === "TERMINATED" && (
                  <Badge
                    title={`employees.employmentStatus.${employee.employmentStatus.toLowerCase()}`}
                    type="danger"
                  />
                )}
                {employee?.employmentStatus === "INACTIVE" && (
                  <Badge
                    title={`employees.employmentStatus.resigned`}
                    type="secondary"
                  />
                )}
              </div>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-400">
              {t("employees.fields.employmentType")}
            </p>
            {isEmployeePanelLoading ? (
              <DetailSkeleton />
            ) : (
              <p className="text-sm font-semibold text-gray-800">
                {t(
                  `employees.employmentType.${
                    employee?.employmentType === "DAILY_WORKER"
                      ? "daily"
                      : employee?.employmentType?.toLowerCase()
                  }`,
                )}
              </p>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-400">
              {t("employees.fields.salaryType")}
            </p>
            {isEmployeePanelLoading ? (
              <DetailSkeleton />
            ) : (
              <p className="text-sm font-semibold text-gray-800">
                {t(
                  `employees.salary.type.${employee?.salaryType?.toLowerCase()}`,
                )}
              </p>
            )}
          </div>
        </div>
      </div>

      {showSalaryTable && annualisation.annualise && (
        <Alert
          type="info"
          title="employees.salaries.annualised.title"
          message={t("employees.salaries.annualised.message", {
            months: annualisation.monthsWorked,
          })}
        />
      )}

      {showSalaryTable && isAttendanceUnavailable && (
        <Alert
          type="warning"
          title="employees.salaries.attendanceUnavailable.title"
          message="employees.salaries.attendanceUnavailable.message"
          renderActions={() => (
            <Link
              to="/v2/accounting/calendars"
              className="inline-block mt-2 text-sm font-medium text-yellow-800 underline hover:text-yellow-900"
            >
              {t("employees.salaries.attendanceUnavailable.action")}
            </Link>
          )}
        />
      )}

      {showSalaryTable && (
        <>
          <div className="flex flex-col">
            <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
                <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                          No
                        </th>
                        <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                          {t("employees.salaries.table.header.month")}
                        </th>
                        <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                          {t("employees.salaries.table.header.salary")}
                        </th>
                        <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                          {t("employees.salaries.table.header.totalAllowances")}
                        </th>
                        <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                          {t("employees.salaries.table.header.taxAllowances")}
                        </th>
                        <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                          {t("employees.salaries.table.header.grossSalary")}
                        </th>
                        <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                          {t(
                            "employees.salaries.table.header.attendanceDeduction",
                          )}
                        </th>
                        <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                          {t("employees.salaries.table.header.totalDeductions")}
                        </th>
                        <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                          {t("employees.salaries.table.header.terCategory")}
                        </th>
                        <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                          {t("employees.salaries.table.header.terRate")}
                        </th>
                        <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                          {t("employees.salaries.table.header.tax")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {monthlySalaries.map((item, index) => (
                        <tr key={index}>
                          <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                            {index + 1}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                            {item.month}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                            {currencyFormat(item.baseSalary)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                            <div className="flex items-center justify-between">
                              <span>
                                {currencyFormat(item.taxableAllowances)}
                              </span>
                              <button
                                onClick={() =>
                                  handleModalOpen(item, "allowance")
                                }
                                className="ml-2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                title={t("employees.allowances.editForMonth", {
                                  month: item.month,
                                })}
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                            {currencyFormat(item.taxAllowance)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                            {currencyFormat(item.grossSalary)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                            {attendanceSummaryLoading ? (
                              <DetailSkeleton />
                            ) : (
                              <>
                                {currencyFormat(item.attendanceDeduction)}
                                {item.isAttendanceDeductionCapped && (
                                  <span
                                    className="block text-xs text-yellow-700"
                                    title={t(
                                      "employees.salaries.deductionCapped.title",
                                    )}
                                  >
                                    {t(
                                      "employees.salaries.deductionCapped.label",
                                      {
                                        amount: currencyFormat(
                                          item.attendanceDeductionUncapped,
                                        ),
                                      },
                                    )}
                                  </span>
                                )}
                              </>
                            )}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                            <div className="flex items-center justify-between">
                              <span>
                                {currencyFormat(item.monthlyDeductionAmount)}
                              </span>
                              <button
                                onClick={() =>
                                  handleModalOpen(item, "deduction")
                                }
                                className="ml-2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                title={t("employees.deductions.editForMonth", {
                                  month: item.month,
                                })}
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                            {item.isDecember ? "-" : `TER ${item.terCategory}`}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                            {item.isDecember
                              ? "-"
                              : `${(item.terRate * 100).toFixed(2)}%`}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                            {currencyFormat(item.taxAmount)}
                          </td>
                        </tr>
                      ))}
                      <tr className="font-medium">
                        <td
                          colSpan={2}
                          className="px-4 py-2 text-center text-sm text-gray-900 whitespace-nowrap"
                        >
                          Total
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                          {currencyFormat(salaryCalculations.totalBaseSalary)}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                          {currencyFormat(
                            salaryCalculations.totalTaxableAllowances,
                          )}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                          {currencyFormat(salaryCalculations.grossUpTaxAmount)}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                          {currencyFormat(salaryCalculations.totalGrossSalary)}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                          {attendanceSummaryLoading ? (
                            <DetailSkeleton />
                          ) : (
                            currencyFormat(
                              salaryCalculations.totalAttendanceDeduction,
                            )
                          )}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                          {currencyFormat(salaryCalculations.totalDeductions)}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                          -
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                          -
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                          {currencyFormat(salaryCalculations.totalTaxAmount)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="space-y-6">
              <div className="bg-white rounded-lg p-4">
                <div className="text-sm text-gray-400 mb-2">
                  {t("employees.salaries.annualGrossSalary")}
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between px-2">
                    <span className="text-sm text-gray-900">
                      {t("employees.salaries.totalAnnualBaseSalaryAllowances")}
                    </span>
                    <span className="text-sm font-medium">
                      {currencyFormat(
                        salaryCalculations.totalBaseSalary +
                          salaryCalculations.totalTaxableAllowances,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between px-2">
                    <span className="text-sm text-gray-900">
                      {t("employees.salaries.totalAnnualTaxAllowances")}
                    </span>
                    <span className="text-sm font-medium">
                      {currencyFormat(salaryCalculations.grossUpTaxAmount)}
                    </span>
                  </div>
                  <div className="border-t pt-2">
                    <div className="flex justify-between p-2 bg-green-50 rounded">
                      <span className="text-sm font-medium text-gray-900">
                        {t("employees.salaries.totalAnnualGrossSalary")}
                      </span>
                      <span className="text-sm font-medium">
                        {currencyFormat(salaryCalculations.totalGrossSalary)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4">
                <div className="text-sm text-gray-400 mb-2">
                  {t("employees.salaries.annualPKP")}
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between px-2">
                    <span className="text-sm text-gray-900">
                      {t("employees.salaries.totalAnnualNetIncome")}
                    </span>
                    <span className="text-sm font-medium">
                      {currencyFormat(salaryCalculations.netSalary)}
                    </span>
                  </div>
                  <div className="flex justify-between px-2">
                    <span className="text-sm text-gray-900">
                      {t("employees.salaries.totalAnnualPositionCost")}
                    </span>
                    <span className="text-sm font-medium text-red-600">
                      -{currencyFormat(salaryCalculations.totalPositionCost)}
                    </span>
                  </div>
                  {salaryCalculations.totalDeductions > 0 && (
                    <div className="flex justify-between px-2">
                      <span className="text-sm text-gray-900">
                        {t("employees.salaries.totalDeductions")}
                      </span>
                      <span className="text-sm font-medium text-red-600">
                        -{currencyFormat(salaryCalculations.totalDeductions)}
                      </span>
                    </div>
                  )}

                  {annualisation.annualise && (
                    <div className="flex justify-between px-2">
                      <span className="text-sm text-gray-900">
                        {t("employees.salaries.annualisedNetIncome", {
                          months: annualisation.monthsWorked,
                        })}
                      </span>
                      <span className="text-sm font-medium">
                        {currencyFormat(
                          salaryCalculations.taxableIncome +
                            salaryCalculations.ptkpAmount,
                        )}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between px-2">
                    <span className="text-sm text-gray-900">PTKP</span>
                    <span className="text-sm font-medium text-red-600">
                      -{currencyFormat(salaryCalculations.ptkpAmount)}
                    </span>
                  </div>
                  <div className="border-t pt-2">
                    <div className="flex justify-between p-2 bg-red-50 rounded">
                      <span className="text-sm font-medium text-gray-900">
                        {t("employees.salaries.totalAnnualPKP")}
                      </span>
                      <span className="text-sm font-semibold text-red-600">
                        {currencyFormat(salaryCalculations.taxableIncome)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4">
                <div className="text-sm text-gray-400 mb-2">
                  {t("employees.salaries.progressiveTax")}
                </div>
                <div className="space-y-2">
                  {TAX_BRACKETS.map((bracket, index) => {
                    const prevMax = index > 0 ? TAX_BRACKETS[index - 1].max : 0;
                    const taxableAmount = Math.min(
                      Math.max(0, pkp - prevMax),
                      bracket.max - prevMax,
                    );

                    if (taxableAmount <= 0) return null;

                    const tax = taxableAmount * bracket.rate;

                    return (
                      <div key={index} className="flex justify-between px-2">
                        <span className="text-sm text-gray-900">
                          {currencyFormat(taxableAmount)} ×{" "}
                          {(bracket.rate * 100).toFixed(0)}%
                        </span>
                        <span className="text-sm font-medium">
                          {currencyFormat(tax.toFixed())}
                        </span>
                      </div>
                    );
                  })}
                  {annualisation.annualise && (
                    <>
                      <div className="flex justify-between px-2 border-t pt-2">
                        <span className="text-sm text-gray-900">
                          {t("employees.salaries.taxBeforeProration")}
                        </span>
                        <span className="text-sm font-medium">
                          {currencyFormat(
                            salaryCalculations.progressiveTaxBeforeProration.toFixed(),
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between px-2">
                        <span className="text-sm text-gray-900">
                          {t("employees.salaries.proration", {
                            months: annualisation.monthsWorked,
                          })}
                        </span>
                      </div>
                    </>
                  )}
                  <div className="border-t pt-2">
                    <div className="flex justify-between p-2 bg-red-50 rounded">
                      <span className="text-sm font-medium text-gray-900">
                        {t("employees.salaries.totalAnnualProgressiveTax")}
                      </span>
                      <span className="text-sm font-semibold text-red-600">
                        {currencyFormat(salaryCalculations.totalTaxAmount)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg p-4">
                <div className="text-sm text-gray-400 mb-2">
                  {t("employees.salaries.lastTaxPeriod")}
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between px-2">
                    <span className="text-sm text-gray-600">
                      {t("employees.salaries.totalAnnualProgressiveTax")}
                    </span>
                    <span className="text-sm font-semibold">
                      {currencyFormat(salaryCalculations.totalTaxAmount)}
                    </span>
                  </div>
                  {/* PPh 21 runs on the calendar year, so an employee whose
                      first month in this year is also their last tax period has
                      no earlier month to total. December of the prior year
                      belongs to that year's 1721-A1 and can never offset this
                      one, so the row would name a range that does not exist.
                      Keyed on the range, not on the amount: a real range that
                      happens to total Rp0 (TER 0%) is worth showing. */}
                  {monthlySalaries.length > 1 && (
                    <div className="flex justify-between px-2">
                      <span className="text-sm text-gray-600">
                        {t("employees.salaries.dateRangeTaxTotal", {
                          startMonth,
                          endMonth: prevMonth,
                        })}
                      </span>
                      <span className="text-sm font-medium">
                        {currencyFormat(salaryCalculations.previousMonthsTax)}
                      </span>
                    </div>
                  )}
                  <div className="border-t pt-2">
                    <div className="flex justify-between p-2 bg-blue-50 rounded">
                      <span className="text-sm font-medium text-gray-900">
                        {t("employees.salaries.totalLastTaxPeriod", {
                          month: endMonth,
                        })}
                      </span>
                      <span className="text-sm font-medium text-blue-600">
                        {currencyFormat(salaryCalculations.currentMonthTax)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Allowance Modal */}
      {allowanceModalOpen && (
        <AllowanceModal
          open={allowanceModalOpen}
          onClose={handleCloseModal}
          employee={employee}
          selectedMonth={selectedMonth}
          selectedYear={currentYear}
        />
      )}

      {/* Deduction Modal */}
      {deductionModalOpen && (
        <DeductionModal
          open={deductionModalOpen}
          onClose={handleCloseModal}
          employee={employee}
          selectedMonth={selectedMonth}
          selectedYear={currentYear}
        />
      )}
    </div>
  );
};

export default SalaryTable;
